if (!customElements.get('announcement-modal-controller')) {
  customElements.define(
    'announcement-modal-controller',
    class AnnouncementModalController extends HTMLElement {
      isDesignMode() {
        return typeof Shopify !== 'undefined' && Shopify.designMode;
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.openTimeout = null;
        this.closeTimeout = null;
        this.modal = null;
        this.originalHide = null;
        this.modalSetup = false;

        this.boundSectionSelect = this.onSectionSelect.bind(this);
        this.boundSectionLoad = this.onSectionLoad.bind(this);
        this.boundModalCloseCapture = this.onModalCloseCapture.bind(this);
        this.boundKeyupCapture = this.onKeyupCapture.bind(this);

        document.addEventListener('shopify:section:select', this.boundSectionSelect);
        document.addEventListener('shopify:section:load', this.boundSectionLoad);
        this.syncModal({ openInEditor: this.isDesignMode() });
      }

      disconnectedCallback() {
        if (this.openTimeout) window.clearTimeout(this.openTimeout);
        if (this.closeTimeout) window.clearTimeout(this.closeTimeout);
        document.removeEventListener('shopify:section:select', this.boundSectionSelect);
        document.removeEventListener('shopify:section:load', this.boundSectionLoad);
        this.teardownModal();
        this.modal?.remove();
      }

      shouldOpenInEditor() {
        return true;
      }

      modalId() {
        return this.dataset.modalId;
      }

      findModal() {
        return document.getElementById(this.modalId()) || this.querySelector('modal-dialog');
      }

      syncModal({ openInEditor = false } = {}) {
        window.requestAnimationFrame(() => {
          const nextModal = this.findModal();
          if (!nextModal) return;

          if (this.modal !== nextModal) {
            this.teardownModal();
            this.modal = nextModal;
            this.setupModal();
          }

          this.removeDuplicateModals();

          if (openInEditor && this.shouldOpenInEditor()) {
            this.scheduleOpen(0);
          }
        });
      }

      setupModal() {
        if (!this.modal || this.modalSetup) return;
        this.modal.dataset.announcementSectionId = this.dataset.sectionId;
        this.originalHide = this.modal.hide.bind(this.modal);
        this.modal.hide = this.handleHide.bind(this);
        this.modal.addEventListener('click', this.boundModalCloseCapture, true);
        this.modal.addEventListener('keyup', this.boundKeyupCapture, true);
        this.modalSetup = true;
      }

      teardownModal() {
        if (!this.modal || !this.modalSetup) return;
        if (this.originalHide) this.modal.hide = this.originalHide;
        this.modal.removeEventListener('click', this.boundModalCloseCapture, true);
        this.modal.removeEventListener('keyup', this.boundKeyupCapture, true);
        this.modalSetup = false;
        this.originalHide = null;
      }

      autoOpenDelay() {
        const delay = Number.parseInt(this.dataset.autoOpenDelay || '0', 10);
        return Number.isNaN(delay) ? 0 : delay;
      }

      scheduleOpen(delay = this.autoOpenDelay()) {
        if (!this.modal) {
          this.syncModal({ openInEditor: true });
          return;
        }
        if (this.openTimeout) window.clearTimeout(this.openTimeout);

        this.openTimeout = window.setTimeout(() => {
          this.modal = this.findModal();
          if (!this.modal) return;
          this.modal.classList.remove('is-closing');
          if (!this.modal.hasAttribute('open')) {
            this.modal.show();
            this.rememberShown();
          }
        }, delay);
      }

      handleHide() {
        this.animateClose();
      }

      storageKey() {
        return `announcement-modal:${this.dataset.sectionId}`;
      }

      removeDuplicateModals() {
        if (!this.modal?.id) return;

        document.querySelectorAll(`modal-dialog#${this.modal.id}`).forEach((modal) => {
          if (modal !== this.modal) modal.remove();
        });
      }

      onModalCloseCapture(event) {
        const closeButton = event?.target?.closest?.('[id^="ModalClose-"]');
        const overlayClick = event?.target === this.modal;
        if (!closeButton && !overlayClick) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.rememberDismissal();
        this.animateClose();
      }

      onKeyupCapture(event) {
        if (event.code?.toUpperCase() !== 'ESCAPE') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.rememberDismissal();
        this.animateClose();
      }

      rememberDismissal() {
        try {
          window.sessionStorage.setItem(this.storageKey(), 'dismissed');
        } catch (error) {}
      }

      rememberShown() {
        if (this.isDesignMode()) return;
        if (this.dataset.showOnce !== 'true') return;
        try {
          window.sessionStorage.setItem(this.storageKey(), 'dismissed');
        } catch (error) {}
      }

      onSectionSelect(event) {
        if (!this.isDesignMode()) return;
        if (event.detail?.sectionId !== this.dataset.sectionId) return;
        if (!this.shouldOpenInEditor()) return;
        this.syncModal({ openInEditor: true });
      }

      onSectionLoad(event) {
        if (!this.isDesignMode()) return;
        if (event.detail?.sectionId !== this.dataset.sectionId) return;
        if (!this.shouldOpenInEditor()) return;
        this.syncModal({ openInEditor: true });
      }

      animateClose() {
        if (!this.modal?.hasAttribute('open')) return;
        if (this.modal.classList.contains('is-closing')) return;

        this.modal.classList.add('is-closing');

        if (this.closeTimeout) window.clearTimeout(this.closeTimeout);
        this.closeTimeout = window.setTimeout(() => {
          this.modal.classList.remove('is-closing');
          this.originalHide();
        }, 260);
      }
    }
  );
}

if (!customElements.get('announcement-carousel')) {
  customElements.define(
    'announcement-carousel',
    class AnnouncementCarousel extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.track = this.querySelector('.announcement-modal-carousel__track');
        this.prevButton = this.querySelector('.announcement-modal-carousel__button--prev');
        this.nextButton = this.querySelector('.announcement-modal-carousel__button--next');
        if (!this.track || !this.prevButton || !this.nextButton) return;

        this.prevButton.addEventListener('click', () => this.scrollByAmount(-1));
        this.nextButton.addEventListener('click', () => this.scrollByAmount(1));
      }

      scrollByAmount(direction) {
        const firstSlide = this.track.querySelector('.announcement-modal-carousel__slide');
        const gap = 12;
        const amount = (firstSlide?.clientWidth || this.track.clientWidth * 0.8) + gap;
        this.track.scrollBy({ left: amount * direction, behavior: 'smooth' });
      }
    }
  );
}
